import { useState } from 'react';
import { CrownSimple, EnvelopeSimple, PencilSimple, Phone, Plus, Trash, X } from '@phosphor-icons/react';
import {
  Auswahl, BestaetigenDialog, FeldRaster, ImmoButton, InnenKarte, KartenTitel, KpiReihe, KpiTile, LeerZustand, SearchSelect, TextFeld,
  ToastPill, useToastPill, type SucheOption,
} from '../../../../../components/immobilien/ui';
import { eur, initialen } from '../../../../../lib/immobilien/format';
import { parseDezimal } from '../../../../../lib/immobilien/stammdaten';
import { useOpenKontakt } from '../../../../../lib/immobilien/openKontakt';
import { entferneEinheitEigentuemer, setzeEinheitEigentuemer } from '../../../../../lib/eigentuemer/verknuepfung';
import { eigentuemerAnzeigename } from '../../../../../lib/eigentuemer/anzeige';
import { kontaktName } from '../../../../../lib/objekte/objektKontakte';
import { supabase } from '../../../../../lib/supabase/client';
import { sucheEigentuemer } from '../../objekt/panels/EigentuemerPanel';
import { useEinheitSeite } from '../EinheitSeitenKontext';
import { DatenZeile } from './teile';

type Anrede = 'Herr' | 'Frau' | 'Firma';
const zahlText = (v: number | null | undefined) => (v == null ? '' : String(v).replace('.', ','));

/**
 * „Eigentümer“ und „Hausgeld“ der Einheit (einheit_eigentuemer, laufende Hauptzeile der Einheit) –
 * dieselben Felder wie der bisherige Eigentümer-Block: Eigentümer wählen oder neu anlegen,
 * MEA, Hausgeld-/Rücklagenvorschuss, SEV-Status; entfernen.
 */
export function EigentuemerBereich({ fokus }: { fokus: 'eigentuemer' | 'hausgeld' }) {
  const s = useEinheitSeite();
  const openKontakt = useOpenKontakt();
  const [toast, zeige] = useToastPill();
  const w = s.daten.eigentuemer;
  const [form, setForm] = useState(false);
  const [entfernen, setEntfernen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);

  const bestaetigeEntfernen = async () => {
    if (!w) return;
    setLaeuft(true);
    try {
      await entferneEinheitEigentuemer(s.daten.einheit.id, w.eigentuemer_id);
      zeige('Eigentümer-Eintrag entfernt');
      setEntfernen(false);
      s.geaendert('eigentuemer');
    } catch (e) {
      zeige(`Nicht entfernt: ${e instanceof Error ? e.message : 'Unbekannt'}`, true);
    } finally {
      setLaeuft(false);
    }
  };

  const eig = w?.eigentuemer ?? null;
  const name = eig ? eigentuemerAnzeigename(eig) : '';

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      {w && fokus === 'hausgeld' && (
        <KpiReihe>
          <KpiTile label="Hausgeld" wert={w.hausgeld_vorschuss != null ? eur(w.hausgeld_vorschuss) : '–'} sub="Vorschuss pro Monat" ton="acc" />
          <KpiTile label="Rücklage" wert={w.ruecklage_vorschuss != null ? eur(w.ruecklage_vorschuss) : '–'} sub="Vorschuss pro Monat" />
          <KpiTile label="MEA" wert={w.mea != null ? w.mea.toLocaleString('de-DE') : '–'} sub="Miteigentumsanteil" />
        </KpiReihe>
      )}

      {form ? (
        <EigentuemerFormular onAbbrechen={() => setForm(false)} onGespeichert={m => { setForm(false); zeige(m); s.geaendert('eigentuemer'); }} onFehler={m => zeige(m, true)} />
      ) : !w ? (
        <LeerZustand icon={CrownSimple} titel="Kein Eigentümer der Einheit" text="Für WEG- und SEV-Einheiten: Eigentümer, MEA und Hausgeld je Einheit festlegen.">
          <ImmoButton art="akzent" icon={Plus} onClick={() => setForm(true)}>Eigentümer festlegen</ImmoButton>
        </LeerZustand>
      ) : (
        <InnenKarte>
          <KartenTitel icon={CrownSimple}>Eigentümer der Einheit</KartenTitel>
          {eig && (
            <button type="button" onClick={() => openKontakt(eig.id, 'eigentuemer')} className="immo-hover-flaeche flex items-center text-left cursor-pointer"
              style={{ gap: 12, padding: 8, margin: -8, borderRadius: 16, border: 'none', background: 'transparent', color: 'var(--ink)' }}>
              <span className="flex items-center justify-center shrink-0" style={{ width: 46, height: 46, borderRadius: '50%', background: 'var(--knob)', fontSize: 14, fontWeight: 600 }}>{initialen(name)}</span>
              <span className="flex flex-col min-w-0">
                <span className="truncate" style={{ fontSize: 16, fontWeight: 800 }}>{name}</span>
                <span className="flex flex-wrap" style={{ gap: 10, fontSize: 12, color: 'var(--ink3)' }}>
                  {eig.email && <span className="flex items-center" style={{ gap: 4 }}><EnvelopeSimple size={12} />{eig.email}</span>}
                  {eig.telefon && <span className="flex items-center" style={{ gap: 4 }}><Phone size={12} />{eig.telefon}</span>}
                </span>
              </span>
            </button>
          )}
          <div className="flex flex-col">
            <DatenZeile label="MEA" wert={w.mea != null ? w.mea.toLocaleString('de-DE') : ''} zahl />
            <DatenZeile label="Hausgeld-Vorschuss" wert={w.hausgeld_vorschuss != null ? eur(w.hausgeld_vorschuss) : ''} zahl />
            <DatenZeile label="Rücklage-Vorschuss" wert={w.ruecklage_vorschuss != null ? eur(w.ruecklage_vorschuss) : ''} zahl />
            {(w.sev_status || s.verwaltungsart === 'SEV' || s.verwaltungsart === 'WEG') && (
              <DatenZeile label="Sondereigentumsverwaltung (SEV)" wert={w.sev_status ?? (s.verwaltungsart === 'WEG' ? 'nein' : '')} />
            )}
          </div>
          <div className="flex flex-wrap" style={{ gap: 8 }}>
            <ImmoButton icon={PencilSimple} onClick={() => setForm(true)}>Ändern</ImmoButton>
            <ImmoButton art="bad" icon={Trash} onClick={() => setEntfernen(true)}>Entfernen</ImmoButton>
          </div>
        </InnenKarte>
      )}

      {fokus === 'eigentuemer' && s.daten.objektEigentuemer.length > 0 && (
        <InnenKarte>
          <KartenTitel rechts="Bearbeiten im Objekt → Eigentümer">Eigentümer des Objekts</KartenTitel>
          <div className="flex flex-col" style={{ gap: 6 }}>
            {s.daten.objektEigentuemer.map(o => (
              <button key={o.id} type="button" onClick={() => openKontakt(o.id, 'eigentuemer')} className="immo-hover-flaeche flex items-center text-left cursor-pointer"
                style={{ gap: 10, padding: 6, borderRadius: 14, border: 'none', background: 'transparent', color: 'var(--ink)' }}>
                <span className="flex items-center justify-center shrink-0" style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--fill2)', fontSize: 12, fontWeight: 700 }}>{initialen(kontaktName(o))}</span>
                <span className="flex flex-col min-w-0">
                  <span className="truncate" style={{ fontSize: 14, fontWeight: 700 }}>{kontaktName(o)}</span>
                  <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{o.email || o.telefon || 'Eigentümer'}</span>
                </span>
              </button>
            ))}
          </div>
        </InnenKarte>
      )}

      {entfernen && (
        <BestaetigenDialog titel="Eigentümer-Eintrag entfernen?" text="MEA und Vorschüsse dieser Einheit werden gelöscht. Der Eigentümer-Kontakt bleibt erhalten."
          detail={name || undefined} bestaetigenLabel="Entfernen" laeuft={laeuft}
          onBestaetigen={() => { void bestaetigeEntfernen(); }} onAbbrechen={() => setEntfernen(false)} />
      )}
    </>
  );
}

function EigentuemerFormular({ onAbbrechen, onGespeichert, onFehler }: { onAbbrechen: () => void; onGespeichert: (meldung: string) => void; onFehler: (meldung: string) => void }) {
  const s = useEinheitSeite();
  const w = s.daten.eigentuemer;
  const vorhanden: SucheOption | null = w?.eigentuemer ? { id: w.eigentuemer.id, label: eigentuemerAnzeigename(w.eigentuemer), typ: 'eigentuemer' } : null;
  const [modus, setModus] = useState<'bestehend' | 'neu'>('bestehend');
  const [gewaehlt, setGewaehlt] = useState<SucheOption | null>(vorhanden);
  const [anrede, setAnrede] = useState<Anrede>('Herr');
  const [vorname, setVorname] = useState('');
  const [nachname, setNachname] = useState('');
  const [firma, setFirma] = useState('');
  const [mea, setMea] = useState(zahlText(w?.mea));
  const [hausgeld, setHausgeld] = useState(zahlText(w?.hausgeld_vorschuss));
  const [ruecklage, setRuecklage] = useState(zahlText(w?.ruecklage_vorschuss));
  const [sev, setSev] = useState(w?.sev_status ?? '');
  const [laeuft, setLaeuft] = useState(false);

  const neuOk = anrede === 'Firma' ? firma.trim() !== '' : nachname.trim() !== '';
  const bereit = modus === 'bestehend' ? gewaehlt !== null : neuOk;

  const speichere = async () => {
    if (!bereit) return;
    setLaeuft(true);
    try {
      let id = gewaehlt?.id ?? null;
      if (modus === 'neu') {
        const { data, error } = await supabase.from('eigentuemer').insert({
          anrede,
          vorname: anrede === 'Firma' ? null : vorname.trim() || null,
          nachname: anrede === 'Firma' ? null : nachname.trim() || null,
          firma_name: firma.trim() || null,
        }).select('id').single();
        if (error) throw new Error(error.message);
        id = (data as { id: string }).id;
      }
      if (!id) throw new Error('Bitte einen Eigentümer wählen');
      await setzeEinheitEigentuemer(s.daten.einheit.id, id, {
        mea: parseDezimal(mea), hausgeld_vorschuss: parseDezimal(hausgeld), ruecklage_vorschuss: parseDezimal(ruecklage), sev_status: sev.trim() || null,
      });
      onGespeichert('Eigentümer der Einheit gespeichert');
    } catch (e) {
      onFehler(`Nicht gespeichert: ${e instanceof Error ? e.message : 'Unbekannt'}`);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <InnenKarte>
      <KartenTitel icon={CrownSimple} rechts={<button type="button" aria-label="Abbrechen" onClick={onAbbrechen} className="flex items-center justify-center cursor-pointer"
        style={{ width: 30, height: 30, borderRadius: '50%', border: 'none', background: 'var(--fill2)', color: 'var(--ink)' }}><X size={14} weight="bold" /></button>}>
        {w ? 'Eigentümer der Einheit ändern' : 'Eigentümer festlegen'}
      </KartenTitel>
      <Auswahl wert={modus} onChange={setModus} optionen={[{ key: 'bestehend', label: 'Bestehender Eigentümer' }, { key: 'neu', label: 'Neu anlegen' }]} />
      {modus === 'bestehend' ? (
        <SearchSelect label="Eigentümer" platzhalter="Name, Firma oder E-Mail …" suche={sucheEigentuemer} onWahl={setGewaehlt} ausgewaehlt={gewaehlt} onEntfernen={() => setGewaehlt(null)} />
      ) : (
        <div className="flex flex-col" style={{ gap: 12 }}>
          <Auswahl label="Anrede" wert={anrede} onChange={setAnrede} optionen={[{ key: 'Herr', label: 'Herr' }, { key: 'Frau', label: 'Frau' }, { key: 'Firma', label: 'Firma' }]} />
          <FeldRaster minBreite={170}>
            {anrede !== 'Firma' && <TextFeld label="Vorname" wert={vorname} onChange={setVorname} />}
            {anrede !== 'Firma' && <TextFeld label="Nachname" wert={nachname} onChange={setNachname} />}
            <TextFeld label={anrede === 'Firma' ? 'Firmenname' : 'Firma (optional)'} wert={firma} onChange={setFirma} />
          </FeldRaster>
        </div>
      )}
      <FeldRaster minBreite={150}>
        <TextFeld label="MEA" wert={mea} onChange={setMea} inputMode="decimal" />
        <TextFeld label="Hausgeld-Vorschuss" wert={hausgeld} onChange={setHausgeld} inputMode="decimal" suffix="€" />
        <TextFeld label="Rücklage-Vorschuss" wert={ruecklage} onChange={setRuecklage} inputMode="decimal" suffix="€" />
        <TextFeld label="SEV (Sondereigentumsverwaltung)" wert={sev} onChange={setSev} placeholder="z. B. SEV – leer = keine SEV" />
      </FeldRaster>
      <div className="flex justify-end flex-wrap" style={{ gap: 8 }}>
        <ImmoButton onClick={onAbbrechen}>Abbrechen</ImmoButton>
        <ImmoButton art="akzent" disabled={!bereit || laeuft} onClick={() => { void speichere(); }}>{laeuft ? 'Speichert …' : 'Speichern'}</ImmoButton>
      </div>
    </InnenKarte>
  );
}
